import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { AppTemplate } from './components/templates';
import { useOverview } from './lib/api';
import { useAuth } from './lib/auth';
import { ClientPage } from './pages/ClientPage';
import { LoginPage } from './pages/LoginPage';
import { OverviewPage } from './pages/OverviewPage';
import { RulesPage } from './pages/RulesPage';
import { ServersPage } from './pages/ServersPage';
import { SettingsPage } from './pages/SettingsPage';

/** Signed-in routes render inside the app template; everyone else is sent to /login. */
function SignedInLayout() {
  const { username, signOut } = useAuth();
  const location = useLocation();
  if (!username) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <SignedInFrame username={username} onSignOut={signOut} />;
}

/** Split out so the overview query only runs once signed in. */
function SignedInFrame({ username, onSignOut }: { username: string; onSignOut: () => void }) {
  const overview = useOverview();
  const queryClient = useQueryClient();
  const fetching = useIsFetching();
  return (
    <AppTemplate
      username={username}
      updatedAt={overview.dataUpdatedAt}
      refreshing={fetching > 0}
      onRefresh={() => void queryClient.invalidateQueries()}
      onSignOut={onSignOut}
    >
      <Outlet />
    </AppTemplate>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<SignedInLayout />}>
        <Route index element={<OverviewPage />} />
        <Route path="servers" element={<ServersPage />} />
        <Route path="rules" element={<RulesPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="clients/:clientId" element={<ClientPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
